import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { debateToolModes } from '@/utils/common'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  SelectTrigger,
} from '@/components/ui/select'
import { EvidenceCard } from './EvidenceCard'
import { EvidenceContentModal } from './EvidenceContentModal'
import { EvidenceSubmissionModal } from './EvidenceSubmissionModal'
import { useHall } from '@/contexts/HallProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import Loader from '@/components/loaders/Loader'
import { Evidence, SpeakingRole } from '@db8/shared'
import { Paginator } from '@/components/ui/paginator'
import { useEvidencePubSub } from '@/hooks/pub-sub/use-evidence-pub-sub'
import { ChannelProvider } from 'ably/react'
import { useAxiosQuery } from '@/hooks/useAxiosQuery'

const EvidenceModal = () => {
  const { setDebateToolMode, debateToolMode } = useMeetingAppContext()

  const [selectedTab, setSelectedTab] = useState<SpeakingRole>(SpeakingRole.Affirmative)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOption, setSortOption] = useState('reliability')
  const [selectedEvidence, setSelectedEvidence] = useState(null)
  const [showSubmissionForm, setShowSubmissionForm] = useState(false)
  const { hall } = useHall()
  const { currentUser } = useHallUsers()
  const [evidenceList, setEvidenceList] = useState<Evidence[]>([])
  const { broadcastEvidence, isFetching } = useEvidencePubSub(setEvidenceList)
  const [currentPage, setCurrentPage] = useState(1)
  const [editingEvidence, setEditingEvidence] = useState(null)
  const ITEMS_PER_PAGE = 12

  const filteredEvidenceFull = evidenceList
    ?.filter((e) => e.team === selectedTab)
    .filter(
      (e) =>
        e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.summary.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      if (sortOption === 'reliability') return b.reliability - a.reliability
      if (sortOption === 'title') return a.title.localeCompare(b.title)
      return 0
    })

  const paginatedEvidence = filteredEvidenceFull?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, sortOption, selectedTab])
  return (
    <>
      <Dialog
        open={debateToolMode === debateToolModes.EVIDENCE}
        onOpenChange={(open) => {
          if (!open) setDebateToolMode(null)
        }}
      >
        <DialogContent
          size="xl"
          className="dark:text-white max-h-[85vh] md:min-w-[600px] lg:min-w-[60vw] max-w-[1000px] min-h-[600px] flex flex-col h-full"
        >
          <DialogTitle className="invisible h-0">Evidence</DialogTitle>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex justify-center sm:justify-start flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => setSelectedTab(SpeakingRole.Affirmative)}
                className={`gradient-bg-affirmative text-white hover:text-white whitespace-nowrap ${
                  selectedTab === SpeakingRole.Affirmative ? 'opacity-100' : 'opacity-40'
                }`}
              >
                {hall.affirmingTeamName}
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedTab(SpeakingRole.Negative)}
                className={`gradient-bg-negative text-white hover:text-white whitespace-nowrap ${
                  selectedTab === SpeakingRole.Negative ? 'opacity-100' : 'opacity-40'
                }`}
              >
                {hall.dissentingTeamName}
              </Button>
            </div>

            <div className="w-full sm:w-auto flex justify-center sm:justify-end mr-8">
              {selectedTab === currentUser?.speakingRole && (
                <Button
                  variant="outline"
                  onClick={() => setShowSubmissionForm(true)}
                  className="whitespace-nowrap"
                >
                  Submit New Evidence
                </Button>
              )}
            </div>
          </div>

          {/* Sort and Search */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 mb-4">
            <Select value={sortOption} onValueChange={(val) => setSortOption(val)}>
              <SelectTrigger className="w-full sm:w-36">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="reliability">Reliability</SelectItem>
                  <SelectItem value="title">Title</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            <input
              type="text"
              placeholder="Search evidence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 rounded-md dark:bg-gray-800 dark:text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:max-w-xs mr-8"
            />
          </div>

          <div className="flex flex-col flex-grow overflow-hidden">
            {isFetching ? (
              <div className="flex items-center justify-center w-full h-full">
                <Loader />
              </div>
            ) : filteredEvidenceFull?.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 w-full px-2 sm:px-4 overflow-y-auto">
                  {paginatedEvidence.map((evidence) => (
                    <EvidenceCard
                      key={evidence.id}
                      evidence={evidence}
                      selectedTab={selectedTab}
                      setSelectedEvidence={setSelectedEvidence}
                    />
                  ))}
                </div>
                <div className="mt-auto pt-4">
                  <Paginator
                    totalItems={filteredEvidenceFull?.length || 0}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    variant="dots"
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-col h-full w-full justify-center items-center text-center">
                <p className="font-bold text-2xl sm:text-3xl">No Evidence Found</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {selectedEvidence && (
        <EvidenceContentModal
          selectedEvidence={selectedEvidence}
          setSelectedEvidence={setSelectedEvidence}
          setEditingEvidence={setEditingEvidence}
          selectedTab={selectedTab}
          onDelete={(evidence) => broadcastEvidence(evidence)}
        />
      )}

      {(showSubmissionForm || editingEvidence) && (
        <EvidenceSubmissionModal
          showSubmissionForm={showSubmissionForm || !!editingEvidence}
          onClose={() => {
            setEditingEvidence(false)
            setShowSubmissionForm(false)
          }}
          setShowSubmissionForm={setShowSubmissionForm}
          initialData={editingEvidence}
          onSubmitComplete={(newEvidence) => broadcastEvidence(newEvidence)}
        />
      )}
    </>
  )
}

export const SyncedEvidenceModal = () => {
  return (
    <ChannelProvider channelName="evidence">
      <EvidenceModal />
    </ChannelProvider>
  )
}
